import { useEffect, useState } from 'react';
import { Link, useParams, useLocation } from 'react-router-dom';
import { useAuth } from '../lib/auth';
import { clientReviewsApi } from '../lib/api/clientReviews';
import { ApiError } from '../lib/api/apiError';
import { PageUnloadingError } from '../lib/api/pageUnloading';
import { isHandledHttpStatus } from '../lib/api/errorLogLevel';
import { log } from '@/lib/logger';
import { fetchApi } from '../lib/api/client';
import { toUserFacingMessage } from '@overlayqa/shared';
import { AppLoadingShell } from '../components/AppLoadingShell';
import { teamJoinRequestsApi, type JoinCandidate } from '../lib/api/teamJoinRequests';
import { capture } from '../lib/analytics';
import { useAppSession } from '../hooks/useAppSession';

type HandoffStatus = 'opening' | 'error';

/**
 * Upper bound on the whole open attempt (getToken + the re-mint POST).
 *
 * Both awaits are unbounded on their own: Clerk's getToken() hits FAPI, and
 * remintReviewHandoff goes through fetchApi, which sets no timeout and no
 * AbortController. The loading branch below renders a spinner and one line of
 * text with no link and no retry, so a stalled request left the user with the
 * browser back button as the only escape. Measured on production 2026-08-12:
 * user-0fa99b@example.com loaded /review-handoff/8876dfde... at 07:48:17 and
 * 19.2s later was back on her issues page, with zero api_network_failure and
 * zero $exception recorded (so the request never failed, it was still pending)
 * and no review_page_viewed for that token ever. She never reached the review
 * she had created 73 seconds earlier.
 *
 * 15s, not the 20s of AUTH_CALLBACK_FAILURE_DEADLINE_MS: that one waits on full
 * Clerk hydration, which measured 13-15s on slow connections. Here ProtectedRoute
 * has already guaranteed isLoaded, so this is a cached-token read plus a POST
 * that does three indexed queries and an HMAC sign. 15s clears a Railway cold
 * start comfortably and still fires before the ~19s point where a real user gave up.
 */
export const HANDOFF_DEADLINE_MS = 15_000;

/**
 * Re-mint bounce for the team-review "Sign in or Create an account" link (Fix 7).
 *
 * A team review that rendered in GUEST mode (expired ?s= handoff, a cookie that
 * failed membership, or the Clerk cookie not crossing the prod app.->api.
 * split) links here. The route is wrapped in ProtectedRoute, so a signed-out
 * user first bounces through /sign-in?redirect_url=... and comes back. On mount
 * we POST /api/review/:token/handoff; the server re-verifies team membership,
 * mints a fresh ?s=, and returns the full review URL - which we assign so the
 * review re-renders authenticated. On failure the server's message (e.g. the
 * membership 403) is surfaced verbatim with a way back to the dashboard.
 */
export function ReviewHandoffPage({ kind = 'review' }: { kind?: 'review' | 'share' }) {
  const { token } = useParams();
  const { search, hash } = useLocation();
  const { getToken } = useAuth();
  const [status, setStatus] = useState<HandoffStatus>('opening');
  const [errorMsg, setErrorMsg] = useState('');
  const [attempt, setAttempt] = useState(0);
  // A forwarded review email reaches colleagues who are not on the owning
  // team (2026-10-05, a same-domain colleague hit the membership 403 and left).
  // On that refusal only, offer the existing same-domain join request.
  const [joinTeams, setJoinTeams] = useState<JoinCandidate[]>([]);
  const [sentTo, setSentTo] = useState<string | null>(null);
  const [joining, setJoining] = useState(false);
  // Account setup (useAppSession -> POST /auth/bootstrap) writes the users row
  // both handoff routes resolve through requireUser. The guest review's "Create
  // an account" link lands here straight after sign-up, and a re-mint sent
  // before that write is answered 401 "Not authenticated" (same class as the
  // 2026-10-16 McpConnectPage incident). Wait for it; a failure gets the error
  // card, whose Try again retries account setup.
  const { data: session, error: sessionError, isFetching: sessionFetching, refetch: refetchSession } = useAppSession();
  const accountReady = !!session;
  const accountError = !accountReady && sessionError && !sessionFetching
    ? toUserFacingMessage(sessionError, kind === 'share' ? 'Could not open this shared issue report.' : 'Could not open this review.')
    : '';

  useEffect(() => {
    if (!token) {
      setStatus('error');
      setErrorMsg(kind === 'share' ? 'This shared issue link is incomplete.' : 'This review link is missing its token.');
      return;
    }
    if (accountError) {
      setStatus('error');
      setErrorMsg(accountError);
      return;
    }

    setStatus('opening');
    setErrorMsg('');
    if (!accountReady) return;

    let cancelled = false;
    // Whichever of {deadline, success, failure} lands first owns the outcome.
    // Without this a slow request that resolved after the deadline would
    // navigate out from under a user who had already been shown the failure.
    let settled = false;

    const deadline = setTimeout(() => {
      if (cancelled || settled) return;
      settled = true;
      log.error('[ReviewHandoffPage] handoff re-mint timed out', {
        timeoutMs: HANDOFF_DEADLINE_MS,
      });
      setStatus('error');
      // Existing fallback copy: the open genuinely did not complete, and the
      // error branch is what carries the way back to the dashboard.
      setErrorMsg(kind === 'share' ? 'Could not open this shared issue report.' : 'Could not open this review.');
    }, HANDOFF_DEADLINE_MS);

    async function openReview() {
      try {
        const clerkToken = (await getToken()) ?? undefined;
        const source = new URLSearchParams(search);
        const context = new URLSearchParams();
        for (const key of ['version', 'rt', 'workflow', 'preview']) {
          const value = source.get(key);
          if (value) context.set(key, value);
        }
        const { url } = kind === 'share'
          ? await fetchApi<{ url: string }>(`/shares/${encodeURIComponent(token!)}/handoff`, { method: 'POST', token: clerkToken, timeoutMs: HANDOFF_DEADLINE_MS })
          : context.toString() ? await clientReviewsApi.remintReviewHandoff(clerkToken, token!, context)
            : await clientReviewsApi.remintReviewHandoff(clerkToken, token!);
        if (cancelled || settled) return;
        settled = true;
        clearTimeout(deadline);
        log.info('[ReviewHandoffPage] handoff re-minted, opening review');
        window.location.assign(hash ? `${url}${hash}` : url);
      } catch (err) {
        if (cancelled || settled) return;
        settled = true;
        clearTimeout(deadline);
        // The route's DESIGNED refusals are handled right here: the message
        // renders with a way back to the dashboard. log.error reaches
        // console.error in prod, which PostHog capture_console_errors files as
        // a fake $exception "crash" (the PR #77/#79 class; measured 2026-08-24
        // on user-fb9528's 403).
        //
        // Which statuses count is NOT restated here. It is isHandledHttpStatus
        // (errorLogLevel.ts), the same set fetchApi's own non-ok branch uses:
        // {401 session lapse, 403 membership refusal, 404 not-found/expired}.
        // This line hardcoded `403 || 404` until 2026-08-25 and so still fired
        // a fake $exception for 401 -- a status this page can genuinely hit,
        // since it sits behind ProtectedRoute and the handoff POST can outlive
        // the session. Two places encoding one rule is how that drift happened;
        // there is now one.
        // Anything else (network, timeout, 5xx) stays loud.
        const status = err instanceof ApiError ? err.status : undefined;
        const logContext = {
          message: err instanceof Error ? err.message : String(err),
          ...(status !== undefined ? { status } : {}),
        };
        if (err instanceof PageUnloadingError) {
          // Our own navigation cancelled the request. The class exists so this
          // can never mint a fake crash ("Nobody reads the message — by
          // definition the page rendering it is gone", pageUnloading.ts), and
          // re-logging it here at any level defeats that. State still settles
          // in case the navigation was cancelled and the page survives.
        } else if (status !== undefined && isHandledHttpStatus(status)) {
          log.warn('[ReviewHandoffPage] handoff re-mint failed', logContext);
        } else {
          log.error('[ReviewHandoffPage] handoff re-mint failed', logContext);
        }
        if (kind === 'review' && status === 403) void loadJoinCandidates();
        setStatus('error');
        setErrorMsg(toUserFacingMessage(err, kind === 'share' ? 'Could not open this shared issue report.' : 'Could not open this review.'));
      }
    }

    async function loadJoinCandidates() {
      try {
        const { candidates } = await teamJoinRequestsApi.candidates((await getToken()) ?? undefined);
        if (cancelled) return;
        const pending = candidates.find(c => c.requested);
        if (pending) setSentTo(pending.name);
        else setJoinTeams(candidates);
        if (candidates.length > 0) capture('review_handoff_join_offer_shown', { candidates: candidates.length });
      } catch (err) {
        // Feature off or not a candidate: the plain refusal stands.
        log.warn('[ReviewHandoffPage] join candidates unavailable', { message: err instanceof Error ? err.message : String(err) });
      }
    }

    void openReview();

    return () => {
      cancelled = true;
      clearTimeout(deadline);
    };
  }, [search, hash, token, getToken, kind, attempt, accountReady, accountError]);

  async function askToJoin(team: JoinCandidate) {
    capture('review_handoff_join_clicked', { team_id: team.teamId });
    setJoining(true);
    try {
      await teamJoinRequestsApi.create((await getToken()) ?? undefined, team.teamId);
      setSentTo(team.name);
    } catch (err) {
      log.warn('[ReviewHandoffPage] join request refused', { message: err instanceof Error ? err.message : String(err) });
      setErrorMsg(toUserFacingMessage(err, 'Could not send the request.'));
    } finally {
      setJoining(false);
    }
  }

  if (status === 'error') {
    return (
      <AppLoadingShell skeleton={false}>
        <section className="card-sm max-w-xl" role="alert">
          <h2 className="text-base font-semibold text-gray-900">
            {kind === 'share' ? 'Shared report unavailable' : joinTeams.length > 0 || sentTo ? 'Request access to this review' : 'Review unavailable'}
          </h2>
          <p className="mt-2 text-sm text-gray-600 break-words">{errorMsg}</p>
          {sentTo && (
            <p className="mt-3 text-sm text-gray-900" role="status">
              Request sent to {sentTo}. We&apos;ll email you when the owner approves.
            </p>
          )}
          <div className="mt-4 flex flex-wrap items-center gap-3">
            {!sentTo && joinTeams.map(team => (
              <button key={team.teamId} type="button" className="btn-primary" disabled={joining} onClick={() => void askToJoin(team)}>
                <span className="text-cap-trim">Ask to join {team.name}</span>
              </button>
            ))}
            <button type="button" className={joinTeams.length > 0 || sentTo ? 'btn-secondary' : 'btn-primary'} onClick={() => { if (!accountReady) void refetchSession(); setAttempt(value => value + 1); }}><span className="text-cap-trim">Try again</span></button>
            <Link to="/dashboard" className="btn-secondary"><span className="text-cap-trim">Back to dashboard</span></Link>
          </div>
        </section>
      </AppLoadingShell>
    );
  }

  return (
    <main className="flex min-h-screen flex-col items-center justify-center gap-3 px-4 text-center">
          <span
            className="h-6 w-6 animate-spin rounded-full border-2 border-primary-500 border-t-transparent"
            aria-hidden="true"
          />
          <p className="text-sm text-gray-500">{kind === 'share' ? 'Opening shared issue report...' : 'Opening review...'}</p>
    </main>
  );
}
